import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocalShipping as ShippingIcon,
  LocalShipping,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
  CheckCircle as VerifiedIcon,
  AccessTime as AccessTimeIcon,
} from '@material-ui/icons';

import { useConsignmentDetailsStyles } from './ConsignmentDetailsPopup.styles';
import { ConsignmentDetailsPopupProps, ConsignmentDetails, ConsignmentHistoryItem } from './redux/ConsignmentDetailsSlice.type';
import { fetchConsignmentDetails, clearConsignmentDetails } from './redux/ConsignmentDetailsSlice';
import { AppState } from '../config/redux/reducers';

const ConsignmentDetailsPopup: React.FC<ConsignmentDetailsPopupProps> = ({
  open,
  onClose,
  consignmentId
}) => {
  const classes = useConsignmentDetailsStyles();
  const dispatch = useDispatch();
  
  // Get state from Redux store
  const {
    consignmentDetails,
    consignmentDetailsLoading,
    consignmentDetailsError
  } = useSelector((state: AppState) => state.consignmentDetails);
  
  // Use Redux state
  const loading = consignmentDetailsLoading;
  const error = consignmentDetailsError;

  useEffect(() => {
    if (open && consignmentId) {
      handleFetchConsignmentDetails();
    }
  }, [open, consignmentId, dispatch]);

  const handleFetchConsignmentDetails = () => {
    if (consignmentId) {
      // Clear existing data first to force fresh fetch
      dispatch(clearConsignmentDetails());
      dispatch(fetchConsignmentDetails({ consignmentId }));
    }
  };

  const handleClose = () => {
    // Clear consignment details when closing
    dispatch(clearConsignmentDetails());
    onClose();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered': return '#4caf50';
      case 'out for delivery': return '#ff9800';
      case 'ready to dispatch': return '#2196f3';
      case 'cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const renderTimelineItem = (item: ConsignmentHistoryItem, index: number, totalItems: number) => {
    // The last item in the chronologically sorted array is the current status
    const isCurrent = index === totalItems - 1;
    // All statuses are considered completed since they happened chronologically
    const isCompleted = true;
    
    const date = new Date(item.timestamp);
    const dateStr = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return (
      <div key={index} className={classes.timelineItem}>
        {/* Date Column */}
        <div className={classes.timelineDate}>
          <div className={classes.timelineDateText}>{dateStr}</div>
          <div className={classes.timelineTimeText}>At {timeStr}</div>
        </div>
        
        {/* Icon Column */}
        <div className={classes.timelineIconContainer}>
          <div
            className={`${classes.timelineIcon} ${
              isCurrent 
                ? classes.timelineIconCurrent 
                : classes.timelineIconCompleted
            }`}
          />
        </div>
        
        {/* Content Column */}
        <div className={classes.timelineContent}>
          <div className={classes.timelineStatus}>{item.status.replace(/_/g, ' ')}</div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography variant="body1">Loading consignment details...</Typography>
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.errorContainer}>
          <Typography variant="h6" color="error">Error</Typography>
          <Typography variant="body1">{error}</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleFetchConsignmentDetails}
            className={classes.retryButton}
          >
            Retry
          </Button>
        </div>
      );
    }

    if (!consignmentDetails) {
      return null;
    }
    return (
      <>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <Typography className={classes.consignmentId}>
              {consignmentId}
            </Typography>
            {consignmentDetails?.orderDetail?.routeNo && (
              <Chip 
                label={consignmentDetails.orderDetail.routeNo}
                className={classes.routeChip}
                variant="outlined"
              />
            )}
            {consignmentDetails?.customerDetails?.area && (
              <Chip 
                label={consignmentDetails.customerDetails.area}
                className={classes.routeChip}
                variant="outlined"
              />
            )}
            {consignmentDetails?.orderDetail?.deliveryDate && (
              <Chip 
                label={consignmentDetails.orderDetail.deliveryDate}
                className={classes.routeChip}
                variant="outlined"
              />
            )}
            {consignmentDetails?.orderDetail?.deliveryEndTime && (
              <Chip 
                label={`Delivery by ${consignmentDetails.orderDetail.deliveryEndTime}`}
                className={classes.routeChip}
                variant="outlined"
              />
            )}
          </div>
          <div className={classes.headerRight}>
            <IconButton className={classes.closeButton} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        
        {/* Status Chips */}
        <div className={classes.statusChipsContainer}>
          <Chip 
            label={consignmentDetails.orderDetail.category} 
            className={classes.categoryChip}
          />
          <Chip 
            label={consignmentDetails.orderDetail.deliveryProposition}
            className={classes.categoryChip}
          />
          <Chip 
            label={consignmentDetails.orderDetail.activity}
            className={classes.categoryChip}
          />
          {/* Live Tracking Button */}
          {consignmentDetails?.liveTracking && (
            <Button 
              className={classes.liveTrackingButton}
              startIcon={<LocationIcon />}
              onClick={() => window.open(consignmentDetails.liveTracking, '_blank')}
              size="small"
            >
              Live Tracking
            </Button>
          )}
        </div>

        {/* Main Content */}
        <div className={classes.content}>
          <div className={classes.mainContent}>
            {/* Left Sidebar */}
            <div className={classes.leftSidebar}>
              {/* Origin Section */}
              <div className={classes.originDestinationCard}>
                <div className={classes.originTitle}>Origin</div>
                
                <div className={classes.locationDetails}>
                  <div className={classes.locationName}>
                    {consignmentDetails.hubDetails.name}
                  </div>
                  <Chip 
                    label={consignmentDetails.hubDetails.storeCode}
                    className={classes.storeCodeChip}
                  />
                  <div className={classes.locationAddress}>
                    {consignmentDetails.hubDetails.address}
                  </div>
                  <div className={classes.locationContact}>
                    {consignmentDetails.driverDetails.primaryDriver.phone}
                  </div>
                </div>
              </div>
              
              {/* Journey Arrow */}
              <div className={classes.journeyArrow}>
                <div className={classes.arrowLine}></div>
                <div className={classes.arrowHead}>↓</div>
              </div>
              
              {/* Destination Section */}
              <div className={classes.originDestinationCard}>
                <div className={classes.destinationTitle}>Destination</div>
                
                <div className={classes.locationDetails}>
                  <div className={classes.locationName}>
                    {consignmentDetails.customerDetails.name}
                    {consignmentDetails.customerDetails.verified && (
                      <VerifiedIcon className={classes.verifiedIcon} />
                    )}
                  </div>
                  <div className={classes.locationContact}>
                    {consignmentDetails.customerDetails.phone}
                  </div>
                  <div className={classes.locationAddress}>
                    {consignmentDetails.customerDetails.address}
                  </div>
                </div>
              </div>
              
              {/* Order Information Card */}
              <div className={classes.originDestinationCard}>
                <div className={classes.orderInfoTitle}>Order Information</div>
                
                <div className={classes.locationDetails}>
                  <div className={classes.orderValueLabel}>Order Value</div>
                  <div className={classes.orderValueAmount}>
                    {consignmentDetails.orderDetail.currency} {consignmentDetails.orderDetail.totalAmount}
                  </div>
                  
                  <div className={classes.paymentTypeLabel}>Payment Type</div>
                  <div className={classes.orderPaymentType}>
                    {consignmentDetails.orderDetail.paymentMode}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Content */}
            <div className={classes.rightContent}>
              {/* Status Section */}
              <div className={classes.statusSection}>
                <div className={classes.statusLabel}>the order is</div>
                <div className={classes.statusTitle}>{consignmentDetails.status}</div>
                <div className={classes.statusDate}>
                  as on {consignmentDetails.consignmentHistory[consignmentDetails.consignmentHistory.length - 1]?.timestamp || 'N/A'}
                </div>
                <div className={classes.statusLastUpdated}>
                  Last updated on {consignmentDetails.consignmentHistory[consignmentDetails.consignmentHistory.length - 1]?.timestamp || 'N/A'}
                </div>
              </div>
              
              {/* Tracking History */}
              <div>
                <Typography className={classes.sectionTitle}>
                  Tracking History
                </Typography>
                <div className={classes.timeline}>
                  {consignmentDetails.consignmentHistory.map((item: ConsignmentHistoryItem, index: number) => 
                    renderTimelineItem(item, index, consignmentDetails.consignmentHistory.length)
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      className={classes.dialog}
    >
      <DialogContent className={classes.dialogContent}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default ConsignmentDetailsPopup;
